import { useEffect, useImperativeHandle, useState } from 'react'

import { computePosition, flip, shift } from '@floating-ui/dom'
import { Editor, ReactRenderer, posToDOMRect } from '@tiptap/react'

import { getItemsWithQuery } from '@/extensions/Mention/lib'
import {
  type MentionItem,
  type MentionListProps,
  type MentionListRef,
  type SuggestionArgs,
  type Suggestions,
} from '@/extensions/Mention/types'

export interface UseSuggestionsProps<T extends MentionItem> {
  component: React.ForwardRefExoticComponent<
    MentionListProps<T> & React.RefAttributes<MentionListRef>
  >
  items: T[]
  suggestion?: SuggestionArgs
  getLabel: (item: T) => string
  onSelect?: (item: T) => void
}

const updatePosition = (editor: Editor, element: Element | null) => {
  if (!element) return

  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      ),
  }

  if (element instanceof HTMLElement) {
    computePosition(virtualElement, element, {
      placement: 'bottom-start',
      strategy: 'absolute',
      middleware: [shift(), flip()],
    }).then(({ x, y, strategy }) => {
      element.style.width = 'max-content'
      element.style.position = strategy
      element.style.left = `${x}px`
      element.style.top = `${y}px`
    })
  }
}

export const useSuggestions = <T extends MentionItem>({
  component: Component,
  items,
  suggestion,
  getLabel,
  onSelect,
}: UseSuggestionsProps<T>): Suggestions => {
  return {
    command: ({ editor, range, props }) => {
      // https://github.com/ueberdosis/tiptap/blob/main/packages/extension-mention/src/mention.ts#L99
      const nodeAfter = editor.view.state.selection.$to.nodeAfter
      const overrideSpace = nodeAfter?.text?.startsWith(' ')

      if (overrideSpace) {
        range.to += 1
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: {
              id: props.id,
              label: props.label,
              type: props.type,
            },
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run()

      // get reference to `window` object from editor element, to support cross-frame JS usage
      editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd()
    },

    items: ({ query }: { query: string }) => {
      return getItemsWithQuery(items, query, getLabel)
    },

    render: () => {
      let component: ReactRenderer<MentionListRef>
      return {
        onStart: (props) => {
          component = new ReactRenderer(Component, {
            props: { ...props, suggestion, getLabel, onSelect },
            editor: props.editor,
          })

          if (!props.clientRect) {
            return
          }

          const element = component.element as HTMLElement
          element.style.position = 'absolute'
          // the list is appended to <body>: modal dialogs set the body to
          // pointer-events none and sit at z-50, and Dialog treats clicks on
          // [data-mention-list] as inside (see components/common/Dialog)
          element.style.pointerEvents = 'auto'
          element.style.zIndex = '60'
          element.setAttribute('data-mention-list', '')

          document.body.appendChild(component.element)

          updatePosition(props.editor, component.element)
        },

        onUpdate(props) {
          if (!component) return

          component.updateProps(props)

          if (!props.clientRect) {
            return
          }

          updatePosition(props.editor, component.element)
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            component.destroy()
            return true
          }

          return component.ref?.onKeyDown(props) ?? false
        },

        onExit() {
          component.element.remove()
          component.destroy()
        },
      }
    },
  }
}

export const useMentionList = <T extends MentionItem>(
  props: MentionListProps<T>,
  ref: React.ForwardedRef<MentionListRef>,
) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  const selectItem = (index: number) => {
    const item = props.items[index]

    if (item) {
      props.command({
        id: item.id,
        label: props.getLabel(item),
        type: item.mentionType,
      })
      props.onSelect?.(item)
    }
  }

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + props.items.length - 1) % props.items.length,
    )
  }

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length)
  }

  const enterHandler = () => {
    selectItem(selectedIndex)
  }

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }

      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        enterHandler()
        return true
      }

      return false
    },
  }))

  useEffect(() => setSelectedIndex(0), [props.items])

  return {
    selectedIndex,
    selectItem,
  }
}
